import { useEffect, useRef, useCallback, useState } from "react";
import { useTheme } from "@/hooks/use-theme";
import { Megaphone } from "lucide-react";

// Declare Canny on window for TypeScript
declare global {
  interface Window {
    Canny?: (action: string, options?: Record<string, unknown>) => boolean | void;
  }
}

const CANNY_APP_ID = "68a63f91a0d91b91c6412d60";

interface CannyChangelogButtonProps {
  position?: "top" | "bottom" | "left" | "right";
  align?: "top" | "bottom" | "left" | "right";
}

export function CannyChangelogButton({
  position = "bottom",
  align = "right",
}: CannyChangelogButtonProps) {
  const { theme } = useTheme();
  const initialized = useRef(false);
  const [isReady, setIsReady] = useState(false);

  // Map our theme to Canny's theme options
  const getCannyTheme = useCallback(() => {
    if (theme === "dark" || theme === "slate") {
      return "dark";
    }
    return "light";
  }, [theme]);

  useEffect(() => {
    let checkInterval: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const initChangelog = () => {
      if (typeof window.Canny === "function" && !initialized.current) {
        try {
          window.Canny("initChangelog", {
            appID: CANNY_APP_ID,
            position,
            align,
            theme: getCannyTheme(),
          });
          initialized.current = true;
          setIsReady(true);
          // Clear timeout since we succeeded
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
        } catch (error) {
          console.error("[Canny] Failed to initialize changelog:", error);
        }
      }
    };

    // Wait for Canny SDK to fully load
    const waitForCanny = () => {
      checkInterval = setInterval(() => {
        // Check if Canny function exists and SDK is loaded
        if (typeof window.Canny === "function") {
          if (checkInterval) clearInterval(checkInterval);
          // Small delay to ensure DOM is ready
          setTimeout(initChangelog, 100);
        }
      }, 200);

      // Cleanup after 15 seconds if Canny doesn't load
      timeoutId = setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
        if (!initialized.current) {
          console.warn("[Canny] SDK failed to load within timeout");
        }
      }, 15000);
    };

    waitForCanny();

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (timeoutId) clearTimeout(timeoutId);
      if (typeof window.Canny === "function") {
        window.Canny("closeChangelog");
      }
    };
  }, [position, align, getCannyTheme]);

  // Re-initialize when theme changes
  useEffect(() => {
    if (initialized.current && typeof window.Canny === "function") {
      window.Canny("closeChangelog");
      initialized.current = false;
      
      setTimeout(() => {
        window.Canny?.("initChangelog", {
          appID: CANNY_APP_ID,
          position,
          align,
          theme: getCannyTheme(),
        });
        initialized.current = true;
      }, 50);
    }
  }, [theme, position, align, getCannyTheme]);

  return (
    <button
      data-canny-changelog
      type="button"
      className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-8 w-8 relative"
      title="What's New"
    >
      <Megaphone className="h-4 w-4" />
    </button>
  );
}
